import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../app/hooks";
import { routes } from "../app/paths";
import { fetchCustomerCompanyByOrgId } from "../features/customer/customerOrganizationsSlice";
import { getCustomerBasicInfoPath } from "../features/customer/getCustomerBasicInfoPath";
import useMessage from "./useMessage";

export const useCustomerNavigation = () => {
  const [isNavigationLoading, setIsNavigationLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();
  const { t } = useTranslation("errors");

  const navigateToCustomer = useCallback(
    async (customerOrgId: string) => {
      setIsNavigationLoading(true);

      const action = await dispatch(
        fetchCustomerCompanyByOrgId({
          customerOrgId,
        }),
      );

      if (isFulfilled(action)) {
        if (action.payload?.customerCompanyId) {
          const navigationUrl = routes.customerBasicInfo(
            action.payload.customerCompanyId,
            { org: customerOrgId },
          );
          window.open(navigationUrl, "_blank");
        } else {
          sendMessage(
            t("customer_company_not_found", { reqId: action.meta.requestId }),
            "error",
          );
        }
      }

      setIsNavigationLoading(false);
    },
    [dispatch, sendMessage, t],
  );

  const navigateToCustomerByUserId = useCallback(
    async (customerUserId: string) => {
      if (isNavigationLoading) return;
      setIsNavigationLoading(true);
      try {
        const path = await getCustomerBasicInfoPath(customerUserId);
        if (path) {
          window.open(path, "_blank", "noopener,noreferrer");
          return;
        }
        sendMessage(t("customer_profile_unavailable"), "error");
      } catch {
        sendMessage(t("customer_profile_unavailable"), "error");
      } finally {
        setIsNavigationLoading(false);
      }
    },
    [isNavigationLoading, sendMessage, t],
  );

  return {
    navigateToCustomer,
    navigateToCustomerByUserId,
    isNavigationLoading,
  };
};
